<!--
  @component

  The text area component

  ```html
  <oc-text-area-v1 name="rating" placeholder="good product at all">Rating</oc-text-area-v1>
  ```

  @slot required `default` Sets a label for the text area. Use it to provide a brief description of the text area. This help users to understand its purpose.
-->
<svelte:options
  customElement={{
    tag: "oc-text-area-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();
          this.attachShadow({ mode: "open", delegatesFocus: true });

          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = (...args) => this.internals.setValidity(...args);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        formResetCallback() {
          this.value = "";
        }
      },
    props: {
      type: { type: "String" },
      placeholder: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      label: { type: "String" },
      hint: { type: "String" },
      variant: { type: "String" },
      required: { type: "Boolean" },
      resizable: { type: "Boolean" },
      maxlength: { type: "Number" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
  }}
/>

<script lang="ts">
  import HintAndCounterV1 from "../../../../common/components/HintAndCounterV1.svelte";

  /**
 *
 */
  export let name: string;

  /**
 *
 *
 *
 *
 *
 */
  export let value: string = "";

  /**
 *
 */
  export let hint: string | undefined = undefined;

  /**
 *
 *
 *
 */
  export let variant: "default" | "error" | "success" | "warning" = "default";

  /**
 *
 *
 *
 */
  export let required: boolean = false;

  /**
 *
 *
 *
 */
  export let disabled: boolean = false;

  /**
 *
 */
  export let resizable: boolean = false;

  /**
 *
 *
 *
 */
  export let placeholder: string | undefined = undefined;
  /*                                                                          */
  $: nonEmptyPlaceholder = placeholder || " ";

  /**
 *
 *
 *
 */
  export let maxlength: number | undefined = undefined;

  /*            */
  export let internals: ElementInternals;

  let textareaElement: HTMLTextAreaElement;

  $: if (textareaElement) {
    internals.setFormValue(value);
    if (!internals?.form?.noValidate) {
      internals.setValidity(
        textareaElement.validity,
        textareaElement.validationMessage,
        textareaElement,
      );
    }
  }

  /*                                       */
  $: ariaDescribedById = hint && "hint";
</script>

<div
  class="text-area"
  class:text-area--error={variant === "error"}
  class:text-area--warning={variant === "warning"}
  class:text-area--success={variant === "success"}
  class:text-area--disabled={disabled}
>
  <textarea
    class="text-area__input"
    class:text-area__input--resizable={resizable}
    id="input"
    bind:this={textareaElement}
    bind:value
    placeholder={nonEmptyPlaceholder}
    {required}
    {disabled}
    {name}
    {maxlength}
    aria-describedby={ariaDescribedById}
  />
  <label class="text-area__label" for="input">
    <slot />
  </label>
</div>

<HintAndCounterV1
  hintId="hint"
  {variant}
  {hint}
  {disabled}
  maxCounter={maxlength}
  counterValue={value}
/>

<style lang="scss">
  @use "TextAreaV1.config" as config;
  @use "@otto-ec/design-tokens/tokens";

  :host {
    @include config.oc-text-area;
  }

  :host[maxlength],
  :host[hint] {
    @include config.oc-text-area-hint;
  }

  .text-area {
    position: relative;

    &__input {
      /*                                                       */
      resize: unset;
      display: block;

      box-sizing: border-box;
      width: 100%;

      height: 84px;
      min-height: 84px;

      padding: tokens.$oc-component-form-field-padding-y tokens.$oc-component-form-field-padding-x;

      background: tokens.$oc-component-form-background-color;
      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border: none;
      border-radius: 8px;

      font: tokens.$oc-component-form-field-input-font;
      color: tokens.$oc-component-form-field-input-color;

      &--resizable {
        resize: vertical;
      }

      &:hover:not(:disabled) {
        outline-width: 2px;
      }

      &:focus {
        outline-color: tokens.$oc-component-form-default-border-color-focus;
        outline-width: 2px;
      }

      &::placeholder {
        font: tokens.$oc-component-form-field-placeholder-font;
        color: tokens.$oc-component-form-field-placeholder-color;
        opacity: 0;
        transition: opacity 0.1s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
      }
    }

    &__label {
      position: absolute;
      top: tokens.$oc-component-form-field-padding-y;
      left: tokens.$oc-semantic-spacing-50;
      padding: 0 tokens.$oc-semantic-spacing-25;
      max-width: 100%;
      box-sizing: border-box;

      transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      user-select: none;
      cursor: text;
      font: tokens.$oc-component-form-label-font;
      background-color: tokens.$oc-component-form-background-color;
      border-radius: tokens.$oc-component-form-field-label-border-radius;
      color: tokens.$oc-component-form-default-label-color;
    }

    &--error {
      .text-area__input {
        outline-color: tokens.$oc-component-form-error-border-color;
      }

      .text-area__label {
        color: tokens.$oc-component-form-error-label-color;
      }
    }

    &--warning {
      .text-area__input {
        outline-color: tokens.$oc-component-form-warning-border-color;
      }
    }

    &--success {
      .text-area__input {
        outline-color: tokens.$oc-component-form-success-border-color;
      }

      .text-area__label {
        color: tokens.$oc-component-form-success-label-color;
      }
    }

    &--disabled {
      .text-area__input {
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;
        color: tokens.$oc-component-form-disabled-input-color;
      }

      .text-area__label {
        background-color: tokens.$oc-component-form-disabled-background-color;
        color: tokens.$oc-component-form-disabled-label-color;
      }
    }

    &__input:focus,
    &__input:not(:placeholder-shown) {
      &::placeholder {
        opacity: 1;
      }

      ~ .text-area__label {
        top: -0.5rem;
        left: tokens.$oc-component-form-field-floating-label-spacing-left;
        padding: 0 tokens.$oc-component-form-field-floating-label-inner-spacing-x;
        font: tokens.$oc-component-form-field-floating-label-font;
      }
    }
  }
</style>
